/**
 * TendersBoardView — Kanban over bid.stage for the workspace.
 *
 * Step 5 of the workflow rollout. Fetches every workspace bid once,
 * joins client-side with the already-loaded RFP list, and renders
 * each tender as a card under its bid's stage column. RFPs without
 * a bid land in the leftmost "No bid yet" column.
 *
 * Read-only for now — clicking a card navigates to the tender
 * workspace where transitions happen on the Workflow tab. Drag-drop
 * transitions are a future enhancement; the gating logic from step 3
 * (artifact approvals) means a casual drag could be blocked anyway,
 * which is a confusing UX without inline feedback.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, FileText, Clock } from "lucide-react";
import { v2Api } from "@/lib/v2Api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

// Same label map + ordering as WorkflowPanel. Limit the board to the
// 8 "active lifecycle" stages — terminal stages (Cancelled / Closed /
// Withdrawn / etc.) get a single combined "Closed" column on the right.
const STAGE_COLUMNS = [
  { code: "_no_bid",          label: "No bid yet" },
  { code: "BID_PUBLISHED",    label: "Published" },
  { code: "BID_PREPARATION",  label: "Preparing" },
  { code: "BID_SUBMITTED",    label: "Submitted" },
  { code: "TECH_EVALUATION",  label: "Tech Eval" },
  { code: "FIN_BID_OPENED",   label: "Financial" },
  { code: "L1_DETERMINED",    label: "L1" },
  { code: "CONTRACT_GENERATED", label: "Contract" },
  { code: "ORDER_PLACED",     label: "Order Placed" },
  { code: "_closed",          label: "Closed" },
];

const TERMINAL_STAGES = new Set([
  "BID_CANCELLED", "BID_WITHDRAWN", "TECH_DISQUALIFIED", "AOC_REJECTED",
  "EMD_FORFEITED", "CONTRACT_TERMINATED", "CONTRACT_CLOSED",
  "DEEMED_ACCEPTED", "PAYMENT_PROCESSED", "CRAC_ISSUED",
]);


export default function TendersBoardView({ rfps }) {
  const navigate = useNavigate();
  const [bids, setBids] = useState([]);
  const [loadingBids, setLoadingBids] = useState(true);

  useEffect(() => {
    let cancelled = false;
    v2Api.bidsWorkspaceList()
      .then((data) => {
        if (cancelled) return;
        setBids(Array.isArray(data) ? data : []);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoadingBids(false); });
    return () => { cancelled = true; };
  }, []);

  // Group RFPs by their bid's stage. Each RFP gets at most one card on
  // the board (we pick the latest bid by stage progression if the user
  // has multiple bids per RFP — rare, but safe).
  const columns = useMemo(() => {
    const bidByRfp = new Map();
    for (const b of bids) {
      if (b.rfp_id) bidByRfp.set(String(b.rfp_id), b);
    }
    const grouped = Object.fromEntries(STAGE_COLUMNS.map((c) => [c.code, []]));
    for (const r of rfps || []) {
      const b = bidByRfp.get(String(r.id));
      let col = "_no_bid";
      if (b) {
        if (TERMINAL_STAGES.has(b.stage)) col = "_closed";
        else if (grouped[b.stage] !== undefined) col = b.stage;
        else col = "BID_PREPARATION"; // unknown active stage → bucket
      }
      grouped[col].push({ rfp: r, bid: b || null });
    }
    return grouped;
  }, [rfps, bids]);

  if (loadingBids) {
    return (
      <div className="p-8 flex items-center gap-2 text-slate-500 text-sm">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading bids…
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <div className="flex gap-3 pb-4 min-w-max">
        {STAGE_COLUMNS.map((col) => {
          const items = columns[col.code] || [];
          return (
            <div
              key={col.code}
              className="w-64 shrink-0 bg-slate-100 rounded-lg p-2"
            >
              <div className="flex items-center justify-between px-2 py-1 mb-1">
                <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                  {col.label}
                </h3>
                <Badge variant="outline" className="text-[10px] bg-white">
                  {items.length}
                </Badge>
              </div>
              <div className="space-y-2">
                {items.length === 0 && (
                  <p className="text-[11px] text-slate-400 italic px-2 py-3 text-center">
                    Empty
                  </p>
                )}
                {items.map(({ rfp, bid }) => (
                  <Card
                    key={rfp.id}
                    className="cursor-pointer hover:border-blue-300 transition border-[#E2E8F0]"
                    onClick={() => navigate(`/tender/${rfp.id}`)}
                  >
                    <CardContent className="p-3 space-y-2">
                      <div className="flex items-start gap-2">
                        <FileText className="w-3.5 h-3.5 text-blue-600 mt-0.5 shrink-0" />
                        <p className="text-xs font-medium text-slate-800 leading-tight line-clamp-2">
                          {rfp.name || rfp.title}
                        </p>
                      </div>
                      {(rfp.tender_no || rfp.portal) && (
                        <p className="text-[10px] text-slate-500 font-mono truncate">
                          {rfp.tender_no || ""}
                          {rfp.portal ? ` · ${rfp.portal}` : ""}
                        </p>
                      )}
                      {bid && (
                        <div className="flex flex-wrap gap-1">
                          {bid.submitted_value_inr ? (
                            <Badge variant="outline" className="text-[10px]">
                              ₹{Number(bid.submitted_value_inr).toLocaleString("en-IN")}
                            </Badge>
                          ) : null}
                          {bid.decision && bid.decision !== "undecided" && (
                            <Badge
                              variant="outline"
                              className={`text-[10px] ${
                                bid.decision === "go"
                                  ? "bg-emerald-50 text-emerald-700"
                                  : bid.decision === "no_go"
                                    ? "bg-rose-50 text-rose-700"
                                    : "bg-slate-100"
                              }`}
                            >
                              {bid.decision}
                            </Badge>
                          )}
                        </div>
                      )}
                      {rfp.submission_deadline && (
                        <p className="text-[10px] text-rose-600 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {rfp.submission_deadline}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
